/**
 * Utilities for parsing and handling vILT Meeting Creation Status
 *
 * The v_ilt_meeting_creation_status field can have the following values:
 * - "PROCESSING" - Meeting creation is in progress
 * - "COMPLETED" - Meeting was created successfully
 * - "ERROR:<message>" - Meeting creation failed with the specified error message
 */

import { ActivityData } from '../../../../interfaces/activity';

export type ViltMeetingStatusType =
    'processing' | 'completed' | 'error' | 'none';

export interface ParsedViltMeetingStatus {
    type: ViltMeetingStatusType;
    message: string | null;
    rawStatus: string | null;
}

const ERROR_PREFIX = 'ERROR:';

/**
 * Parses the v_ilt_meeting_creation_status field to extract status type and error message
 *
 * @param status - The raw v_ilt_meeting_creation_status value
 * @returns Parsed status with type and optional error message
 *
 * @example
 * parseViltMeetingStatus('COMPLETED') // { type: 'completed', message: null, rawStatus: 'COMPLETED' }
 * parseViltMeetingStatus('ERROR:WebEx API failed') // { type: 'error', message: 'WebEx API failed', rawStatus: 'ERROR:WebEx API failed' }
 */
export const parseViltMeetingStatus = (
    status: string | null | undefined,
): ParsedViltMeetingStatus => {
    if (!status) {
        return { type: 'none', message: null, rawStatus: null };
    }

    if (status === 'COMPLETED') {
        return { type: 'completed', message: null, rawStatus: status };
    }

    if (status === 'PROCESSING') {
        return { type: 'processing', message: null, rawStatus: status };
    }

    if (status.startsWith(ERROR_PREFIX)) {
        const errorMessage = status.substring(ERROR_PREFIX.length).trim();
        return {
            type: 'error',
            message: errorMessage || 'An unknown error occurred',
            rawStatus: status,
        };
    }

    // Handle any other status as-is (shouldn't happen but provides safety)
    return { type: 'none', message: status, rawStatus: status };
};

/**
 * Returns a user-friendly display message for the vILT meeting status
 */
export const getViltMeetingStatusDisplayText = (
    parsedStatus: ParsedViltMeetingStatus,
): string => {
    switch (parsedStatus.type) {
        case 'completed':
            return 'WebEx meeting created';
        case 'processing':
            return 'Creating WebEx meeting...';
        case 'error':
            return parsedStatus.message || 'WebEx meeting creation failed';
        case 'none':
        default:
            return 'Waiting for WebEx meeting';
    }
};

/**
 * A person who is granted WebEx co-host access on the auto-created vILT meeting.
 */
export interface ViltMeetingCoHost {
    name: string;
    email: string;
    /** The activity role this co-host derives from, for display context. */
    role: 'Instructor' | 'Additional owner' | 'Operations owner';
}

/**
 * Returns true when the vILT meeting creation error indicates a missing WebEx
 * host license. The backend (VCSP/Aurous) returns a fixed message for this case:
 *   "VCSP Error 400: One or more instructors do not have an active WebEx host
 *    license. Please verify host licenses in WebEx Admin and retry."
 * Matching is intentionally loose (substring, case-insensitive) so minor backend
 * wording changes don't silently disable the co-host guidance.
 */
export const isWebexLicenseError = (
    parsedStatus: ParsedViltMeetingStatus,
): boolean => {
    if (parsedStatus.type !== 'error' || !parsedStatus.message) {
        return false;
    }
    const message = parsedStatus.message.toLowerCase();
    return (
        message.includes('webex host license') ||
        message.includes('active webex')
    );
};

/**
 * Collects every person the backend adds as a WebEx co-host on the auto-created
 * vILT meeting. This MUST mirror the backend's co-host derivation
 * (AwsGrimsbyVCMSServiceLambda _get_co_hosts_attributes): instructors, then
 * additional owners, then the operations owner. Each of these needs an active
 * WebEx Meetings Suite license on awsvirtual.webex.com or meeting creation
 * fails. De-duplicated by email (first role wins), preserving that ordering.
 */
export const getMeetingCoHosts = (
    activity: ActivityData | null | undefined,
): ViltMeetingCoHost[] => {
    const byEmail = new Map<string, ViltMeetingCoHost>();

    const add = (
        name: string | null | undefined,
        email: string | null | undefined,
        role: ViltMeetingCoHost['role'],
    ) => {
        const trimmedEmail = email?.trim();
        if (!trimmedEmail) {
            return;
        }
        const key = trimmedEmail.toLowerCase();
        if (byEmail.has(key)) {
            return;
        }
        byEmail.set(key, {
            name: name?.trim() || trimmedEmail,
            email: trimmedEmail,
            role,
        });
    };

    for (const instructor of activity?.instructors || []) {
        add(instructor.name, instructor.email, 'Instructor');
    }

    for (const owner of activity?.additional_owners || []) {
        add(
            owner.additional_owner_name,
            owner.additional_owner_email,
            'Additional owner',
        );
    }

    add(
        activity?.operations_owner,
        activity?.operations_owner_email,
        'Operations owner',
    );

    return Array.from(byEmail.values());
};
